import { useMemo, useState } from 'react';
import { Search, Filter, RotateCw } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Skeleton,
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
} from '@vnetwork/ui/primitives';
import {
  EmptyState,
  FilterBuilder,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from './WaapEmptyCard';
import {
  useWaapThreatCharts,
  useWaapThreatFilterOptions,
  THREAT_TIME_RANGES,
  type ThreatTimeRange,
  type WaapThreatFilters,
  type WaapTableChart,
  type WaapTableRow,
  type WaapAccessLogArgs,
} from '@/api/waap';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WaapChartGrid } from './WaapAnalyticsBoard';
import { DetailsLogTable } from './DetailsLogTable';
import { LogDetailModal } from './LogDetailModal';
import styles from './ThreatInvestigationTab.module.css';

// Every access-log field the vendor filters on, ordered exactly like the vendor.
// Each becomes a multi-select condition in the Filters popover; the option lists
// are fetched server-side. Domain and Origin Response Time are built separately
// (websites come from the binding; response time is a `>=` bound, not a choice).
const FIELDS = [
  { key: 'src_ip', label: 'Source IP' },
  { key: 'upstream_addr', label: 'Origin IP' },
  { key: 'request_uuid', label: 'Request ID' },
  { key: 'path', label: 'Path' },
  { key: 'attack_type', label: 'Attack Type' },
  { key: 'action', label: 'HTTP Method' },
  { key: 'status', label: 'Status' },
  { key: 'user_agent', label: 'User Agent' },
  { key: 'referer', label: 'Referer' },
  { key: 'manual_rule_id', label: 'Manual Rule Id' },
  { key: 'inject_payload', label: 'Inject Payload' },
  { key: 'inject_attack_type', label: 'Inject Attack Type' },
  { key: 'country', label: 'Region' },
  { key: 'in_whitelist_ip', label: 'In Whitelist IP' },
  { key: 'inject_rule_id', label: 'Inject Rule Id' },
] as const;

/** Condition field for the Origin Response Time bound — a `>=` seconds value,
 *  and the name the API expects for it. */
const RESPONSE_TIME_FIELD = 'upstream_response_time_min';

/**
 * Analytics → Threat Investigation — NATIVE charts (no Superset iframe) over the
 * vendor's access log.
 *
 * The vendor stacks all 17 filters in a permanent left rail; that rail squeezed
 * the (already 20-column) Details table into a sliver, and nobody uses more than
 * a couple of the fields at a time. So the board gets the full width and the
 * filters move into the toolbar's Filters popover — the same shape every other
 * list in the console uses. Time Range stays out in the open: it is the one
 * control everybody touches.
 */
export function ThreatInvestigationTab() {
  const { selected, websites, isLoading: websitesLoading } = useWaapWebsite();
  const domainId = selected?.domainId;

  const [range, setRange] = useState<ThreatTimeRange>('Last 4 hours');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // One condition row per field/value pair; a multiselect row carries its values
  // comma-joined, so a field's scope is every row for it, flattened.
  const collect = useMemo(
    () => (field: string) =>
      conditions
        .filter((c) => c.field === field)
        .flatMap((c) => (c.value || '').split(','))
        .map((s) => s.trim())
        .filter(Boolean),
    [conditions],
  );

  const domains = useMemo(() => collect('domain'), [collect]);
  const filters = useMemo(() => {
    const out: WaapThreatFilters = {};
    for (const f of FIELDS) {
      const values = collect(f.key);
      if (values.length) out[f.key] = values;
    }
    const [respTime] = collect(RESPONSE_TIME_FIELD);
    if (respTime && respTime !== '0') out[RESPONSE_TIME_FIELD] = respTime;
    return out;
  }, [collect]);

  const { data: optionsData } = useWaapThreatFilterOptions(domainId, range, domains);
  const options = optionsData ?? {};
  const { data, isLoading, isError, error, refetch, isFetching } = useWaapThreatCharts(
    domainId,
    range,
    domains,
    filters,
  );

  // Only "is any of": the board's filters reach Superset as `op: IN`, so an
  // exclusion operator would silently read as an inclusion.
  const multiselectField = (key: string, label: string): FilterField => ({
    value: key,
    label,
    input: 'multiselect',
    operators: [{ value: 'is_any_of', label: 'is any of' }],
    options: (options[key] ?? []).map((o) => ({ value: o, label: o || '(empty)' })),
  });

  // FilterBuilder seeds its first condition row from fields[0], so Request ID
  // leads: it is the field people reach for, the same one the search box above
  // looks up. The rest keep the vendor's order.
  const filterFields = useMemo<FilterField[]>(
    () => [
      multiselectField('request_uuid', 'Request ID'),
      {
        value: 'domain',
        label: 'Domain',
        input: 'multiselect',
        options: websites.map((w) => ({ value: w.domain, label: w.domain })),
      },
      ...FIELDS.filter((f) => f.key !== 'request_uuid').map((f) =>
        multiselectField(f.key, f.label),
      ),
      {
        value: RESPONSE_TIME_FIELD,
        label: 'Origin Response Time (s)',
        operators: [{ value: 'gte', label: '≥' }],
      },
    ],
    [websites, options],
  );

  // Click a Details row → open the Log Detail modal for that request. The
  // Details table is pulled out of the generic grid and rendered clickable; the
  // rest of the board's widgets still go through WaapChartGrid.
  const [detail, setDetail] = useState<{ args: WaapAccessLogArgs; row: WaapTableRow } | null>(null);
  const detailsChart = useMemo(
    () =>
      (data?.charts ?? []).find(
        (c): c is WaapTableChart => c.kind === 'table' && c.key === 'details',
      ),
    [data],
  );
  const restCharts = useMemo(
    () => (data?.charts ?? []).filter((c) => c !== detailsChart),
    [data, detailsChart],
  );
  const openDetail = (row: WaapTableRow) => {
    // Rows carry request_uuid as `request_id`, hostname as `host`, and epoch
    // millis as the hidden `timestamp_ms` — all three key the detail lookup (the
    // vendor requires timestampMs).
    const requestUuid = row.request_id != null ? String(row.request_id) : '';
    if (!requestUuid) return;
    const ts = row.timestamp_ms != null ? Number(row.timestamp_ms) : undefined;
    setDetail({
      args: {
        requestUuid,
        hostname: row.host != null ? String(row.host) : '',
        timestampMs: Number.isFinite(ts) ? ts : undefined,
      },
      // Kept so the modal can fall back to the row when the provider's lookup
      // fails — the row already holds every column the Details table queried.
      row,
    });
  };

  // Search by the id we hand the visitor: the WAF block page prints
  // "Error ID: <uuid>", which is the record's `request_uuid`. The Request ID
  // filter inside the popover is a multi-select of server-supplied options, so a
  // pasted id can't be picked there — typing it here writes the same
  // `request_uuid` condition by hand, which narrows both the trend and the log
  // exactly like the vendor's own Request ID filter does.
  const [search, setSearch] = useState('');
  const applySearch = () => {
    const requestUuid = search.trim();
    setConditions((cur) => {
      const rest = cur.filter((c) => c.field !== 'request_uuid');
      return requestUuid
        ? [...rest, { id: `search-${requestUuid}`, field: 'request_uuid', operator: 'is_any_of', value: requestUuid }]
        : rest;
    });
  };

  if (websitesLoading) return <Skeleton height={520} />;
  if (!domainId) {
    return (
      <WaapEmptyCard
        icon={<Search size={20} />}
        title="No websites yet"
        description="Add a website under this account to investigate its threat logs."
      />
    );
  }

  return (
    <>
      <Toolbar
        start={
          <>
            <Select value={range} onValueChange={(v) => setRange(v as ThreatTimeRange)}>
              <SelectTrigger size="md" style={{ width: 170, maxWidth: '100%' }} aria-label="Time Range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THREAT_TIME_RANGES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tooltip content="A blocked visitor sees this ID on the 403 page. Paste it to narrow the board to that request.">
              <Input
                startSlot={<Search size={15} />}
                placeholder="Search by Request ID"
                aria-label="Search by Request ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
                wrapperClassName={styles.lookupInput}
              />
            </Tooltip>
            <Button variant="secondary" onClick={applySearch}>
              Search
            </Button>
          </>
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className={styles.filterPopoverContent}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    const next = c.filter((x) => x.value.trim());
                    setConditions(next);
                    // The search box is just the request_uuid condition's editor —
                    // follow whatever the popover left behind.
                    setSearch(
                      next.find((x) => x.field === 'request_uuid')?.value.split(',')[0]?.trim() ?? '',
                    );
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setConditions([]);
                  setSearch('');
                }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="secondary"
              startIcon={<RotateCw size={15} />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Skeleton height={480} />
      ) : data === null ? (
        <EmptyState
          icon={<Search size={20} />}
          title="Threat Investigation not configured"
          description="The Threat Investigation board is not set up for this environment yet. Ask an administrator to enable it."
        />
      ) : isError ? (
        <Alert tone="danger" title="Couldn't load threat investigation">
          {errMessage(error)}
        </Alert>
      ) : (
        <div className={styles.board}>
          <WaapChartGrid charts={restCharts} />
          {detailsChart && <DetailsLogTable chart={detailsChart} onRowClick={openDetail} />}
        </div>
      )}

      <LogDetailModal
        domainId={domainId}
        args={detail?.args ?? null}
        row={detail?.row ?? null}
        columns={detailsChart?.columns}
        open={!!detail}
        onOpenChange={(o) => {
          if (!o) setDetail(null);
        }}
      />
    </>
  );
}
